/** All user-facing copy, in one place so it can be reviewed and translated later. */
export const strings = {
  common: {
    back: 'Back',
    settings: 'Settings',
    appName: 'Foe Names',
  },
  /** Speaking practice, shared by every screen where the child says words. */
  speech: {
    sayThisWord: 'Say this word out loud',
    /** Exercises (content/exercises.ts). */
    finishSentence: 'Finish the sentence',
    solveRiddle: 'Solve the riddle: say the answer',
    mysteryWord: 'Mystery word',
    hearWord: 'Hear the word',
    showMeHow: 'Show me how',
    chooseNext: 'You choose! Say one, or tap it.',
    selfCheck: 'Which one did you hear? Tap it.',
    quieterSpot: 'Is there a quieter spot you could go to?',
    /** While the child's attempt plays back, before the answers appear. */
    listenFirst: 'Listen…',
    /** Puts an open question away without answering. */
    skipQuestion: 'Skip',
    /** The syllable step for a word: "An easier step for rock". */
    easierStep: (word: string) => `An easier step for “${word}”`,
    micStart: 'Start speaking',
    micStop: 'Stop',
  },
  /** Pronunciation feedback headlines; `sound` is a label such as "/r/". */
  feedback: {
    loading: (percent: number) => `Scanner loading… ${String(percent)}%`,
    micOff: 'Tap the mic to start',
    starting: 'Starting the scanner…',
    yourTurn: 'Your turn!',
    listening: 'Listening…',
    hearing: 'I hear you…',
    checking: 'Checking…',
    /** A question is open: answer it by tapping. */
    tapAnswer: 'Tap your answer',
    great: (sound: string) => `Great ${sound} sound!`,
    closer: 'Closer!',
    notYet: 'Not quite yet',
    notHeard: 'I couldn’t hear that',
    justTheWord: 'Just the word, please',
    newWord: 'New word!',
    complete: 'Mission complete!',
    error: 'Scanner problem',
    unsupported: (missing: readonly string[]) =>
      `This browser can't run the scanner (missing: ${missing.join(', ')}). Try the latest Chrome, Firefox or Safari.`,
    soundsLabel: 'Sounds in this word',
    soundCorrect: 'sounded right',
    soundToPractise: 'needs practice',
    targetSound: 'target sound',
    practiseSyllable: (syllable: string) => `Practise: ${syllable}`,
  },
  /** The Foe Elevator (level progress). */
  intro: {
    /** The intro screen before the first mission (features/intro). */
    title: 'Top secret briefing',
    begin: 'Start the briefing',
    go: 'Start the mission',
    replay: 'Hear it again',
    resume: 'Continue the briefing',
  },
  elevator: {
    titleTop: 'Foe',
    titleBottom: 'Elevator',
    tagline: 'Ride up. Meet the foes.',
    needs: (floor: number) => `Practise floor ${String(floor)}`,
    lockedLabel: (floor: number, from: number) =>
      `Floor ${String(floor)}, locked. It opens when you practise floor ${String(from)} and say half of its words with a strong r.`,
    floorLabel: (floor: number, focus: string, stars: number) =>
      `Floor ${String(floor)}: ${focus}. ${String(stars)} of 3 stars.`,
    floorTitle: (floor: number) => `Floor ${String(floor)}`,
    practice: 'Mission words',
    start: 'Start mission',
    nextStar: [
      'Finish the mission for your first star.',
      'Say half the words with a strong r for 2 stars.',
      'Say most words right on the first try for 3 stars.',
      'All 3 stars! Play again any time.',
    ] as const,
  },

  /** Grown-ups' settings. */
  settings: {
    title: 'Grown-ups',
    ageQuestion: 'How old is the player?',
    ageLabel: (age: number) => (age >= 13 ? '13+' : String(age)),
    ageNote:
      'Sets how the coach talks and how strict the scanner is. Young voices are harder to judge, so the scanner is a little more forgiving up to age 8.',
    reset: 'Start over',
    resetConfirm: 'Clear all stars and codenames on this device?',
    resetYes: 'Clear progress',
    resetNo: 'Keep',
    resetDone: 'Progress cleared.',
    debug: 'Debug tools',
    done: 'Done',
  },

  /** QA test sessions (grown-ups; docs/design/qa-recording.md). */
  qa: {
    title: 'Test session',
    explain:
      'Records the microphone, what the app says and its decisions, to find problems. Stays on this device until it is uploaded to the developer computer.',
    start: 'Start test session',
    recording: (elapsed: string) => `Recording ${elapsed}`,
    noteLabel: 'What happened? (optional)',
    notePlaceholder: 'e.g. at 0:42 it said "one word" when nobody spoke',
    stop: 'Stop and save',
    pending: 'Saved test sessions',
    upload: 'Upload',
    download: 'Download',
    remove: 'Delete',
  },

  /** End of a mission. */
  mission: {
    complete: 'Mission complete!',
    /** What was achieved, never a label for the child (coaching-framework.md: process praise). */
    starLabels: ['Mission complete', 'Strong r’s', 'Strong r’s, first try'] as const,
    starHints: [
      'Say half of the words with a strong r for 2 stars: that uncovers the whole name and opens the next floor.',
      'Say most words with a strong r on the first try for 3 stars.',
      'All 3 stars: strong r’s on the first try.',
    ] as const,
    /** Compared only with the child's own best (mastery, not comparison with others). */
    beatBest: (now: number, before: number) =>
      `${String(now)} strong r’s: more than your best before (${String(before)})!`,
    matchedBest: (now: number) => `${String(now)} strong r’s: as many as your best!`,
    /** Effort and achievement, both informational (rewards-and-level-model.md). */
    practised: (attempts: number, total: number) =>
      `You practised ${String(attempts)} ${attempts === 1 ? 'time' : 'times'}` +
      (total > attempts ? ` (${String(total)} on this floor so far).` : '.'),
    /** The r signal over the mission: honest about a dip, which is normal while learning. */
    signal: (from: number, to: number) =>
      to > from + 0.02
        ? 'Your r signal went up!'
        : to < from - 0.02
          ? 'Your r signal dipped a little. Signals go up and down while you learn.'
          : 'Your r signal held steady.',
    streak: (n: number) => `Longest run: ${String(n)} strong r’s in a row!`,
    keepPractising: (before: number) =>
      `Your best is ${String(before)} strong r’s. Practice makes your r stronger!`,
    newBest: 'New best!',
    wordsRight: (correct: number, total: number) =>
      `${String(correct)} of ${String(total)} ${total === 1 ? 'word' : 'words'} with a strong r`,
    elevator: 'Elevator',
    again: 'Play again',
    unlocked: (floor: number) => `Floor ${String(floor)} unlocked!`,
  },
  codename: {
    /** The mastery meter in the foe card (mastery.ts). */
    signal: 'r signal',
    complete: 'Name revealed!',
  },
  /** The transmission stage (epic #44, docs/design/transmission.md). */
  transmission: {
    title: 'Transmit the codename',
    /** The headline while Scout listens. */
    sayIt: 'Say the codename to Scout',
    send: 'Send the codename',
    /** Hear the codename read aloud (the child's choice, for help reading it). */
    hear: 'Hear the name',
    received: 'Codename received!',
    scout: 'Scout, your teammate at headquarters',
    /** The bonus star on the results, shown apart from the mission stars. */
    bonus: 'Clear r bonus!',
  },
  scanner: {
    titleTop: 'Voiceprint',
    titleBottom: 'Scanner',
  },
  hint: {
    /** The mouth picture's heading, above the word being practised. */
    title: 'How to make the r',
    shows: 'Like this',
    insteadOf: 'Not this',
    replay: 'Hear it again',
    playAgain: 'Tap to play it again',
    /** A cue step asks for a sound: "Your turn: ee". */
    yourTurn: (sound: string) => `Your turn: ${sound}`,
    /** What to look at in each picture, in a few words (under it). */
    look: {
      'r-bunched': 'Middle of the tongue up high, back low',
      'r-retroflex': 'Tongue tip up, not touching',
      w: 'Lips round, back of the tongue up',
      l: 'Tongue tip touching behind the teeth',
      'tongue-sides': 'Sides of the tongue on the back teeth',
    },
    diagrams: {
      'r-bunched':
        'Side view of the mouth for r: the middle of the tongue bunched up high, the back low',
      'r-retroflex':
        'Side view of the mouth for r: the tongue tip pointing up, not touching, the back low',
      w: 'Side view of the mouth for w: lips pushed forward and round, the back of the tongue up',
      l: 'Side view of the mouth for l: the tongue tip touching the ridge behind the top teeth',
      'tongue-sides':
        'The roof of the mouth seen from below: the sides of the tongue press along the back teeth, the air goes down the middle',
    },
  },
} as const;
